import { useTenant } from '@emerald-works-nova/auth'
import { useEvent } from '@emerald-works/react-event-bus-client'
import Gleap from 'gleap'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useAlert, useIsOnTrial, useIsStudentPlan, usePaidConsumer } from '../../hooks'
import { onboardingSlice, userSlice, subscriptionSlice } from '../../reducers'
import useGroup from '../../hooks/useGroup'
import { ALL_ENTERPRISE } from '../../services/cognito/userGroups'
import { useMindset } from '../mindset-agent/mindset-context'
import GleapChecklist from './gleap-checklist'
import consts from '../../consts'

const GleapContext = createContext()

const EXCLUDED_ROUTES = ['/signup', '/login']
const PLAYLIST_ROUTE = '/playlist'

const ChecklistAutoHide = () => {
  const { isOpen: isMindsetAgentOpen } = useMindset()
  const { isOpen: isChecklistOpen, setOpen, hasHidden, setHasHidden } = useContext(GleapContext)
  const { enqueueMsg } = useAlert()
  const location = useLocation()

  useEffect(() => {
    if ((isMindsetAgentOpen || location.pathname.startsWith(PLAYLIST_ROUTE)) && isChecklistOpen && !hasHidden) {
      setOpen(false)
      setHasHidden(true)
      enqueueMsg('Your checklist has been hidden to make room', 'info')
    }
  }, [isMindsetAgentOpen, location.pathname, isChecklistOpen, hasHidden, setOpen, setHasHidden, enqueueMsg])

  useEffect(() => {
    if (!isMindsetAgentOpen && !location.pathname.startsWith(PLAYLIST_ROUTE) && hasHidden) {
      setHasHidden(false)
    }
  }, [isMindsetAgentOpen, location.pathname, hasHidden, setHasHidden])

  return null
}

const GleapWidgetAutoHide = () => {
  const { isOpen: isMindsetAgentOpen } = useMindset()
  const { isOpen: isChecklistOpen, setOpen: setChecklistOpen } = useContext(GleapContext)
  const { enqueueMsg } = useAlert()
  const [hasHiddenWidget, setHasHiddenWidget] = useState(false)
  const [wasWidgetOpen, setWasWidgetOpen] = useState(false)

  // Hide widget when M:Coach opens
  useEffect(() => {
    if (isMindsetAgentOpen && !hasHiddenWidget) {
      const checkAndClose = async () => {
        try {
          const isGleapOpen = await Gleap.isOpened()
          if (isGleapOpen) {
            Gleap.close()
            setHasHiddenWidget(true)
            enqueueMsg('The Help widget has been hidden to make room for Ask M:Coach', 'info')
          }
        } catch (error) {
          // Gleap might not be initialized yet
        }
      }
      checkAndClose()
    }
  }, [isMindsetAgentOpen, hasHiddenWidget, enqueueMsg])

  // Hide widget when Checklist opens
  useEffect(() => {
    if (isChecklistOpen) {
      const checkAndClose = async () => {
        try {
          const isGleapOpen = await Gleap.isOpened()
          if (isGleapOpen) {
            Gleap.close()
          }
        } catch (error) {
          // Gleap might not be initialized yet
        }
      }
      checkAndClose()
    }
  }, [isChecklistOpen])

  // Close checklist when widget opens
  useEffect(() => {
    const checkWidgetState = async () => {
      try {
        const isGleapOpen = await Gleap.isOpened()

        // If widget just opened and checklist is open, close the checklist
        if (isGleapOpen && !wasWidgetOpen && isChecklistOpen) {
          setChecklistOpen(false)
        }

        setWasWidgetOpen(isGleapOpen)
      } catch (error) {
        // Gleap might not be initialized yet
      }
    }

    const interval = setInterval(checkWidgetState, 500)
    return () => clearInterval(interval)
  }, [isChecklistOpen, wasWidgetOpen, setChecklistOpen])

  useEffect(() => {
    if (!isMindsetAgentOpen && hasHiddenWidget) {
      setHasHiddenWidget(false)
    }
  }, [isMindsetAgentOpen, hasHiddenWidget])

  return null
}

const GleapProvider = ({ children }) => {
  const gleapChecklistRef = React.useRef(null)
  const { isPaidConsumer } = usePaidConsumer()
  const { isOnTrial } = useIsOnTrial()
  const { isStudentPlan } = useIsStudentPlan()
  const { isInGroup } = useGroup()
  const tenant = useTenant()
  const location = useLocation()
  const [initialized, setInitialized] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const [hasBeenOpened, setHasBeenOpened] = useState(false)
  const [hasSkipped, setHasSkipped] = useState(false)
  const [hasDismissed, setHasDismissed] = useState(false)
  const [hasHidden, setHasHidden] = useState(false)
  const isGleapEnabled = process.env.REACT_APP_GLEAP_ENABLED === 'true'
  const showChecklist = useSelector(onboardingSlice.selectors.selectChecklist)
  const onboardingProfile = useSelector(onboardingSlice.selectors.selectOnboardingProfile)
  const accountInfoLoaded = useSelector(subscriptionSlice.selectors.selectAccountInfoLoaded)
  const profile = useSelector(userSlice.selectors.selectProfile)

  const [onboardingTrackEvent] = useEvent([onboardingSlice.eventBus.onboardingChecklistTrackEvent])
  const [checklistCompleted] = useEvent([{ eventName: 'checklistCompleted' }])

  const isNewUser = useMemo(() => {
    if (!profile?.subscription?.activatedAt) return false
    return profile.subscription.activatedAt > (Date.now() - 30 * 24 * 60 * 60 * 1000)
  }, [profile?.subscription?.activatedAt])

  const isOnExcludedRoute = useMemo(() => {
    return EXCLUDED_ROUTES.some(route => location.pathname.startsWith(route))
  }, [location.pathname])

  const isChecklistDismissed = useMemo(() => {
    return hasDismissed || onboardingProfile?.onboarding?.checklist?.onboarded
  }, [onboardingProfile?.onboarding?.checklist?.onboarded, hasDismissed])

  const activeChecklistId = useMemo(() => {
    const studentChecklistId = process.env.REACT_APP_GLEAP_STUDENT_CHECKLIST_ID
    const defaultChecklistId = process.env.REACT_APP_GLEAP_CHECKLIST_ID

    if (isStudentPlan === null) return null

    if (isStudentPlan === true && studentChecklistId) {
      return studentChecklistId
    }

    return defaultChecklistId
  }, [isStudentPlan])

  const shouldShowChecklist = useMemo(() => {
    return initialized && activeChecklistId && isPaidConsumer && showChecklist && !isOnExcludedRoute && !isChecklistDismissed && isNewUser
  }, [initialized, activeChecklistId, isPaidConsumer, showChecklist, isOnExcludedRoute, isChecklistDismissed, isNewUser])

  const shouldAutoShowChecklist = useMemo(() => {
    return shouldShowChecklist && !hasBeenOpened && !location.pathname.startsWith(PLAYLIST_ROUTE)
  }, [shouldShowChecklist, hasBeenOpened, location.pathname])

  const showDismiss = useMemo(() => {
    return hasSkipped || onboardingProfile?.onboarding?.checklist?.viewCount >= 2
  }, [hasSkipped, onboardingProfile?.onboarding?.checklist?.viewCount])

  const handleComplete = useCallback(() => {
    onboardingTrackEvent.trigger({
      type: 'onboardedChecklist'
    })
    checklistCompleted.trigger()
    setOpen(false)
    setHasDismissed(true)
  }, [onboardingTrackEvent, checklistCompleted])

  const setOpenWithTracking = (open) => {
    if (!hasBeenOpened && open) {
      setHasBeenOpened(true)
    }
    setOpen(open)
  }

  const handleOpen = useCallback(() => {
    Gleap.open()
  }, [])

  const handleSkip = useCallback(() => {
    if (!hasSkipped) {
      onboardingTrackEvent.trigger({
        type: 'dismissChecklist'
      })
      setHasSkipped(true)
    } else {
      onboardingTrackEvent.trigger({
        type: 'onboardedChecklist'
      })
      setHasDismissed(true)
    }
    setOpen(false)
  }, [onboardingTrackEvent, hasSkipped])

  useEffect(() => {
    if (initialized || !isGleapEnabled) return

    Gleap.initialize(process.env.REACT_APP_GLEAP_API_KEY)
    Gleap.on('initialized', () => {
      setInitialized(true)
    })
  }, [initialized, isGleapEnabled])

  useEffect(() => {
    const isEnterprise = isInGroup(ALL_ENTERPRISE) && tenant?.toolkitType === consts.TOOLKIT_TYPE.enterprise
    const canIdentify = initialized && profile?.username && (accountInfoLoaded || isEnterprise)
    if (canIdentify) {
      Gleap.identify(`${tenant?.pk}#${profile.username}`, {
        customData: {
          isPaidUser: isPaidConsumer && !isOnTrial,
          isTrialUser: isOnTrial && tenant?.pk === consts.DEFAULT_CLUB_PK,
          isEnterpriseUser: isEnterprise,
          isStudentPlanUser: isStudentPlan
        }
      })
      Gleap.trackEvent('userIdentified')
    }
  }, [initialized, profile?.username, accountInfoLoaded, tenant?.pk, isOnTrial, isPaidConsumer, isStudentPlan, isInGroup, tenant?.toolkitType])

  useEffect(() => {
    if (!initialized || !showChecklist || !activeChecklistId) return

    Gleap.getChecklistData(activeChecklistId, profile?.userSk)
      .then((data) => {
        if (data.status === 'done') {
          handleComplete()
        }
      })
  }, [initialized, activeChecklistId, showChecklist, handleComplete, profile?.userSk])

  useEffect(() => {
    if (!shouldAutoShowChecklist || isOpen) return

    const timer = setTimeout(async () => {
      // Close the widget if it's open before showing the checklist
      try {
        const isGleapOpen = await Gleap.isOpened()
        if (isGleapOpen) {
          Gleap.close()
        }
      } catch (error) {
        // Gleap might not be initialized yet, continue anyway
      }

      setOpen(true)
      setHasBeenOpened(true)
    }, 2000)

    return () => clearTimeout(timer)
  }, [shouldAutoShowChecklist, isOpen])

  useEffect(() => {
    if (!isOpen) return

    const checkCompletion = () => {
      const container = document.querySelector('[data-test="gleap-checklist-container"]')
      const gleapElement = container?.querySelector('gleap-checklist')
      const contentElement = gleapElement?.shadowRoot?.querySelector('.checklists-content')

      if (contentElement?.classList.contains('checklists-content--done')) {
        clearInterval(intervalId)
        setTimeout(handleComplete, 5000)
      }
    }

    const intervalId = setInterval(checkCompletion, 1000)
    return () => clearInterval(intervalId)
  }, [isOpen, handleComplete])

  return (
    <GleapContext.Provider value={{
      gleapChecklistRef,
      isGleapEnabled,
      hasBeenOpened,
      isOpen,
      setOpen: setOpenWithTracking,
      shouldShowChecklist,
      hasHidden,
      setHasHidden,
      handleOpen
    }}
    >
      <>
        <ChecklistAutoHide />
        <GleapWidgetAutoHide />
        {shouldShowChecklist && (
          <GleapChecklist
            id={activeChecklistId}
            open={isOpen}
            setOpen={setOpenWithTracking}
            onSkip={handleSkip}
            showDismiss={showDismiss}
            userId={profile?.userSk}
            ref={gleapChecklistRef}
          />
        )}
        {children}
      </>
    </GleapContext.Provider>
  )
}

const useGleap = () => {
  const {
    gleapChecklistRef,
    isOpen,
    hasBeenOpened,
    shouldShowChecklist,
    handleOpen,
    setOpen
  } = useContext(GleapContext)
  const { isPaidConsumer } = usePaidConsumer()
  const profile = useSelector(userSlice.selectors.selectProfile)
  const isGleapEnabled = process.env.REACT_APP_GLEAP_ENABLED === 'true'

  const openChecklist = useCallback(async () => {
    if (isOpen || !shouldShowChecklist) {
      return
    }

    // Close the widget if it's open
    try {
      const isGleapOpen = await Gleap.isOpened()
      if (isGleapOpen) {
        Gleap.close()
      }
    } catch (error) {
      // Gleap might not be initialized yet, continue anyway
    }

    setOpen(true)
  }, [isOpen, shouldShowChecklist, setOpen])

  const closeChecklist = useCallback(() => {
    if (isOpen) {
      setOpen(false)
    }
  }, [isOpen, setOpen])

  const trackEvent = useCallback((eventName) => {
    if (isPaidConsumer && isGleapEnabled) {
      Gleap.trackEvent(eventName, {
        userId: profile?.userSk,
        timestamp: new Date().toISOString()
      })
    }
  }, [isPaidConsumer, isGleapEnabled, profile?.userSk])

  return {
    openChecklist,
    closeChecklist,
    trackEvent,
    gleapChecklistRef,
    isOpen,
    hasBeenOpened,
    shouldShowChecklist,
    handleOpen
  }
}

export { GleapProvider, useGleap }
